import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ParentChildProvider } from '../../contexts/ParentChildContext';
import {
  FiHome, FiCalendar, FiBook, FiBarChart2, FiFileText,
  FiAlertCircle, FiCreditCard, FiBell, FiMessageSquare, FiLogOut, FiUser, FiMenu, FiX
} from 'react-icons/fi';
import api from '../../services/api';

const ParentLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    api.get('/parent-portal/notifications/unread-count')
      .then((res) => { if (res.data.success) setUnreadCount(res.data.count || 0); })
      .catch(() => {});
    api.get('/parent-portal/messages/unread-count')
      .then((res) => { if (res.data.success) setUnreadMessages(res.data.count || 0); })
      .catch(() => {});
  }, [location.pathname]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const navItems = [
    { name: 'Accueil', shortName: 'Accueil', href: '/parent/dashboard', icon: FiHome },
    { name: 'Évaluations', shortName: 'Évals', href: '/parent/evaluations', icon: FiBook },
    { name: 'Résultats', shortName: 'Notes', href: '/parent/notes', icon: FiBarChart2 },
    { name: 'Documents', shortName: 'Docs', href: '/parent/documents', icon: FiFileText },
    { name: 'Absences', shortName: 'Absences', href: '/parent/absences', icon: FiAlertCircle },
    { name: 'Scolarité', shortName: 'Frais', href: '/parent/fees', icon: FiCreditCard },
    { name: 'Messages', shortName: 'Messages', href: '/parent/messages', icon: FiMessageSquare },
    { name: 'Rendez-vous', shortName: 'RDV', href: '/parent/appointments', icon: FiCalendar },
  ];

  const NavLink = ({ item, compact = false }) => {
    const active = location.pathname === item.href;
    const Icon = item.icon;
    const badge = item.href === '/parent/messages' ? unreadMessages : 0;

    if (compact) {
      return (
        <Link
          to={item.href}
          className={`flex flex-col items-center justify-center gap-0.5 flex-1 min-w-0 py-1 px-0.5 text-[10px] font-medium transition-colors ${
            active ? 'text-primary-600' : 'text-slate-500'
          }`}
        >
          <span className="relative">
            <Icon size={18} />
            {badge > 0 && (
              <span className="absolute -top-1.5 -right-2 min-w-[14px] h-3.5 flex items-center justify-center text-[9px] font-bold text-white bg-red-500 rounded-full px-0.5">
                {badge > 9 ? '9+' : badge}
              </span>
            )}
          </span>
          <span className="truncate w-full text-center">{item.shortName}</span>
        </Link>
      );
    }

    return (
      <Link
        to={item.href}
        className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
          active
            ? 'border-primary-600 text-primary-600'
            : 'border-transparent text-slate-500 hover:text-slate-700'
        }`}
      >
        <Icon size={15} />
        {item.name}
        {badge > 0 && (
          <span className="ml-1 min-w-[16px] h-4 flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full px-1">
            {badge > 9 ? '9+' : badge}
          </span>
        )}
      </Link>
    );
  };

  return (
    <ParentChildProvider>
      <div className="min-h-screen bg-slate-50 pb-16 md:pb-0">
        <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-30">
          <div className="max-w-5xl mx-auto px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-bold text-slate-900 truncate">Espace Parents</h1>
              <p className="text-xs text-slate-500">Schola</p>
            </div>
            <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
              <Link
                to="/parent/notifications"
                className="relative p-2 text-slate-500 hover:text-primary-600 transition-colors"
                title="Notifications"
              >
                <FiBell size={18} />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full px-1">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Link>
              <span className="text-sm text-slate-600 hidden sm:flex items-center max-w-[120px] lg:max-w-none truncate">
                <FiUser className="inline mr-1 flex-shrink-0" />
                {user?.prenom} {user?.nom}
              </span>
              <button
                onClick={logout}
                className="hidden sm:flex items-center gap-1 text-sm text-slate-500 hover:text-red-600"
              >
                <FiLogOut size={16} /> Déconnexion
              </button>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100"
                aria-label="Menu"
              >
                {mobileMenuOpen ? <FiX size={20} /> : <FiMenu size={20} />}
              </button>
            </div>
          </div>

          {/* Nav desktop / tablette */}
          <nav className="hidden md:flex max-w-5xl mx-auto px-4 gap-0.5 pb-0 overflow-x-auto">
            {navItems.map((item) => (
              <NavLink key={item.href} item={item} />
            ))}
          </nav>

          {/* Menu mobile déroulant */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-slate-100 bg-white px-3 py-2 max-h-[60vh] overflow-y-auto">
              <div className="flex items-center gap-2 px-2 py-2 mb-2 border-b border-slate-100 sm:hidden">
                <FiUser className="text-slate-400" />
                <span className="text-sm text-slate-700 truncate">{user?.prenom} {user?.nom}</span>
              </div>
              {navItems.map((item) => {
                const active = location.pathname === item.href;
                const Icon = item.icon;
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium ${
                      active ? 'bg-primary-50 text-primary-700' : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <Icon size={18} />
                    {item.name}
                  </Link>
                );
              })}
              <button
                onClick={logout}
                className="flex items-center gap-3 px-3 py-3 w-full text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg mt-1"
              >
                <FiLogOut size={18} /> Déconnexion
              </button>
            </div>
          )}
        </header>

        <main className="max-w-5xl mx-auto px-3 sm:px-4 py-4 sm:py-6 min-w-0">
          <Outlet />
        </main>

        {/* Bottom navigation mobile */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] safe-bottom">
          <div className="flex items-stretch justify-around px-1 pt-1 pb-1">
            {navItems.map((item) => (
              <NavLink key={item.href} item={item} compact />
            ))}
          </div>
        </nav>
      </div>
    </ParentChildProvider>
  );
};

export default ParentLayout;
